import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { Oval } from "react-loader-spinner";
import { showNotification } from "../../components/Notification";
import { formatLabel } from "../../uitls/Utils";
import { resetPassword, getPasswordRequirements } from "../../api/userApi";

import { Tooltip } from "@mui/material";
import {
  faCheck,
  faTimes,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { userState, selectPasswordRequirements } from "../user/userSlice";
import PasswordRequirements from "../../components/PasswordRequirements";

const EmployeeResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Get email from location state or localStorage
  const email =
    location.state?.email ||
    JSON.parse(localStorage.getItem("user") || "{}")?.corporateEmail;

  const [formValues, setFormValues] = useState({ corporateEmail: email });
  const [formErrors, setFormErrors] = useState({});

  const [pwd, setPwd] = useState("");
  const [matchPwd, setMatchPwd] = useState("");
  const [validPwd, setValidPwd] = useState(true);

  const [validMatch, setValidMatch] = useState(false);
  const [submitReferenceState, setSubmitReferenceState] = useState(false);

  const stateOfUser = useSelector(userState);
  const passwordRequirements = useSelector(selectPasswordRequirements);

  // Fetch password requirements on component mount
  useEffect(() => {
    if (passwordRequirements.length === 0) {
      dispatch(getPasswordRequirements());
    }
  }, [dispatch, passwordRequirements.length]);

  // Format requirements for tooltip
  const requirementsText = passwordRequirements.length > 0 
    ? passwordRequirements.join(". ") 
    : "Must include uppercase and lowercase letters, a number and a special character. Allowed special characters: !@#$%*";

  useEffect(() => {
    if (
      !stateOfUser.error &&
      submitReferenceState &&
      stateOfUser.status === "succeeded"
    ) {
      showNotification("success", "Password reset successfully...");
      navigate("/signIn");
    }
    setSubmitReferenceState(false);
  }, [submitReferenceState]);

  const requiredInputFields = {
    otp: true,
    password: true,
  };

  const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!*@#$%]).{8,24}$/;
  useEffect(() => {
    const result = PWD_REGEX.test(matchPwd);
    if (result && validMatch) {
      setFormValues((prevValues) => ({ ...prevValues, password: matchPwd }));
      setFormErrors((prevValues) => ({ ...prevValues, password: "" }));
    } else {
      setFormValues((prevValues) => ({ ...prevValues, password: undefined }));
    }
  }, [pwd, matchPwd, validMatch]);

  useEffect(() => {
    const result = PWD_REGEX.test(pwd);
    setValidPwd(result);
    const match = pwd === matchPwd;
    setValidMatch(match);
  }, [pwd, matchPwd]);

  const trimObjectValues = (obj) => {
    if (Array.isArray(obj)) {
      return obj.map(trimObjectValues);
    } else if (typeof obj === "object" && obj !== null) {
      return Object.keys(obj).reduce((acc, key) => {
        acc[key] = trimObjectValues(obj[key]);
        return acc;
      }, {});
    } else if (typeof obj === "string") {
      return obj.trim();
    }
    return obj;
  };

  // Helper function to validate email format
  const validateEmail = (value, key) => {
    const emailFields = ["email", "personalEmail", "corporateEmail"];
    if (emailFields.includes(key) && value) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailPattern.test(value) ? "" : "Invalid email format";
    }
    return "";
  };

  // Helper function to validate password
  const validatePassword = (value, key) => {
    if (key !== "password") return "";

    if (!value) {
      return !validPwd ? "Enter a valid Password" : "";
    }

    if (!validPwd) {
      if (value.length < 8) {
        return "Password must have atleast 8 characters!";
      }
      return "Enter a valid Password";
    }

    return "";
  };

  // Helper function to validate phone number
  const validatePhoneNumber = (value, key) => {
    const phoneFields = ["phoneNumber", "contactNumber"];
    if (phoneFields.includes(key) && value) {
      return value.length === 10 ? "" : "Phone Number must have 10 Digits!";
    }
    return "";
  };

  // Helper function to validate string fields
  const validateStringField = (value, key) => {
    const trimmedValue = value.trim();

    if (!trimmedValue) {
      return `${formatLabel(key)} is required`;
    }

    // Check email validation
    const emailError = validateEmail(trimmedValue, key);
    if (emailError) return emailError;

    // Check password validation
    const passwordError = validatePassword(trimmedValue, key);
    if (passwordError) return passwordError;

    // Check phone number validation
    const phoneError = validatePhoneNumber(trimmedValue, key);
    if (phoneError) return phoneError;

    return "";
  };

  // Helper function to validate non-string fields
  const validateNonStringField = (value, key) => {
    if (value === null || value === undefined || value === "") {
      return `${formatLabel(key)} is required`;
    }
    return "";
  };

  // Helper function to validate a single field
  const validateField = (key) => {
    const value = formValues[key];

    if (!requiredInputFields?.[key]) {
      return "";
    }

    if (typeof value === "string") {
      return validateStringField(value, key);
    }

    return validateNonStringField(value, key);
  };

  const validateForm = () => {
    const validationErrors = {};

    Object.keys(requiredInputFields)?.forEach((key) => {
      const error = validateField(key);
      if (error) {
        validationErrors[key] = error;
      }
    });

    setFormErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      dispatch(resetPassword(trimObjectValues(formValues)))
        .unwrap()
        .then(() => {
          setSubmitReferenceState(true);
        })
        .catch((error) => {
          console.error("Error:", error);
          showNotification(
            "error",
            error?.message || "Something went wrong..."
          );
        });
    }
  };

  const handleChange = (name) => (e) => {
    setFormValues((prevValues) => ({ ...prevValues, [name]: e.target.value }));

    // Extract nested ternary operation into independent statement
    let errorMessage = "";
    if (requiredInputFields[name]) {
      if (e.target.value.trim()) {
        errorMessage = "";
      } else {
        errorMessage = `${formatLabel(name)} is required`;
      }
    } else {
      errorMessage = "";
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: errorMessage,
    }));
  };

  return (
    <div className="h-auto flex items-start justify-center p-4 md:p-8">
      {/* Main Container with Shadow */}
      <div className="w-full max-w-6xl bg-white rounded-2xl md:rounded-3xl shadow-2xl overflow-hidden relative">
        <div className="flex flex-col lg:flex-row h-full">
          {/* Form Section - Left */}
          <div className="w-full lg:w-1/2 flex items-start justify-center p-6 md:p-8 lg:p-16">
            <div className="w-full max-w-md">
              {/* Form Title */}
              <div className="text-center mb-4 md:mb-6 lg:mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 md:mb-3">
                  Reset Your Password
                </h2>
                <p className="text-sm md:text-base text-gray-600">
                  Enter verification code and proceed with changing your
                  password
                </p>
              </div>

              {/* Description */}
              <div className="mb-6 md:mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-600 text-center">
                  Have you forgotten the password? Enter verification code and
                  proceed with changing your password.
                </p>
              </div>

              {/* Form Fields */}
              <div className="space-y-4 md:space-y-6">
                {/* Verification Code Field */}
                <div>
                  <label
                    htmlFor="verification-code"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Verification Code
                  </label>
                  <div className="relative">
                    <input
                      id="verification-code"
                      type="text"
                      placeholder="Enter verification code"
                      value={formValues?.otp || ""}
                      onChange={handleChange("otp")}
                      className="block w-full px-3 md:px-4 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-[#135e96] focus:border-transparent transition-all duration-200 text-base md:text-lg"
                    />
                  </div>
                  {formErrors?.otp && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      Verification code is required
                    </p>
                  )}
                </div>

                {/* Password Field */}
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Password<span className="text-red-600">*</span>
                    <Tooltip
                      placement="top"
                      title={requirementsText}
                    >
                      <span className="ml-1">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-blue-gray-700"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Valid Password">
                      <span
                        className={validPwd ? "text-[#32CD32] ml-1" : "hidden"}
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Invalid Password">
                      <span
                        className={
                          validPwd || !pwd ? "hidden" : "text-red-600 ml-1"
                        }
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </span>
                    </Tooltip>
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      type="password"
                      placeholder="Enter your password"
                      value={pwd}
                      onChange={(e) => setPwd(e.target.value)}
                      className="block w-full px-3 md:px-4 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-[#135e96] focus:border-transparent transition-all duration-200 text-base md:text-lg"
                    />
                  </div>
                  {formErrors?.password && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      {formErrors.password}
                    </p>
                  )}
                  {/* Show password requirements when user starts typing */}
                  {pwd && (
                    <div className="mt-2">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4">
                        <p className="text-sm font-semibold text-blue-900 mb-2">
                          Password Requirements:
                        </p>
                        <PasswordRequirements 
                          className="text-blue-800" 
                          showTitle={false}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div>
                  <label
                    htmlFor="confirm-password"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Confirm Password<span className="text-red-600">*</span>
                    <Tooltip
                      placement="top"
                      title="Must match the New password."
                    >
                      <span className="ml-1">
                        <FontAwesomeIcon
                          icon={faInfoCircle}
                          className="text-blue-gray-700"
                        />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Valid Match">
                      <span
                        className={
                          validMatch && matchPwd && validPwd
                            ? "text-[#32CD32] ml-1"
                            : "hidden"
                        }
                      >
                        <FontAwesomeIcon icon={faCheck} />
                      </span>
                    </Tooltip>
                    <Tooltip placement="top" title="Invalid Match">
                      <span
                        className={
                          (validMatch && validPwd) || !matchPwd
                            ? "hidden"
                            : "text-red-600 ml-1"
                        }
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </span>
                    </Tooltip>
                  </label>
                  <div className="relative">
                    <input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm your password"
                      value={matchPwd}
                      onChange={(e) => setMatchPwd(e.target.value)}
                      className="block w-full px-3 md:px-4 py-3 md:py-4 border border-gray-300 rounded-lg md:rounded-xl focus:ring-2 focus:ring-[#135e96] focus:border-transparent transition-all duration-200 text-base md:text-lg"
                    />
                  </div>
                  {formErrors?.password && (
                    <p className="mt-1 md:mt-2 text-sm text-red-600">
                      {formErrors.password}
                    </p>
                  )}
                </div>
              </div>

              {/* Back to Profile Link */}
              <div className="flex justify-center">
                <p className="text-sm text-gray-600 mt-5">
                  Want to go back?{" "}
                  <Link
                    className="text-[#F5A623] hover:text-[#ffcf70] font-medium"
                    to="/user-profile"
                  >
                    Back to Profile
                  </Link>
                </p>
              </div>

              {/* Loading Indicator */}
              <div className="flex justify-center items-center my-3 md:my-4">
                {stateOfUser?.loading && (
                  <Oval
                    height={35}
                    width={35}
                    color={"#1f7fbb"}
                    ariaLabel="loading"
                    strokeWidth={3}
                  />
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={!validMatch || !validPwd || stateOfUser?.loading}
                className="w-full -mt-3 py-3 md:py-4 px-4 brand-btn-primary rounded-lg md:rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed text-base md:text-lg ripple"
              >
                {stateOfUser?.loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-2 border-white border-t-transparent mr-2"></div>
                    <span>Resetting...</span>
                  </div>
                ) : (
                  "Reset Password"
                )}
              </button>
            </div>
          </div>

          {/* Blue Design Section - Right - Hidden on Mobile */}
          <div className="hidden lg:block w-1/2 relative overflow-hidden">
            {/* Large Blue Abstract Shape */}
            <div className="absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 w-[180%] h-[122%] brand-sidebar rounded-full"></div>

            {/* White Text Overlay */}
            <div className="relative z-10 h-full flex flex-col justify-center items-center px-16">
              <div className="text-white text-center max-w-md">
                <h1 className="text-5xl font-bold mb-4 tracking-wide ml-13">
                  Password Reset
                </h1>
                <p className="text-xl leading-relaxed text-left mb-8 ml-15">
                  Secure Your Account, Restore Your Access
                </p>
                <div className="space-y-3 text-left ml-15">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Secure Verification</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Strong Password Policy</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Account Recovery</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-blue-300 rounded-full mr-3"></div>
                    <span className="text-lg">Data Protection</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Small Blue Spheres - Outside Abstract Shape - Hidden on Mobile */}
        <div className="hidden lg:block absolute bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-12 z-20 ml-4">
          <div className="w-16 h-16 brand-sidebar rounded-full"></div>
          <div className="w-12 h-12 brand-sidebar rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeResetPassword;
